import React, { useState, useEffect,useCallback } from "react";

import {
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { oauthUserMgmt, tenant } from "../../utils/utils";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { oauthHeaders, headers } from "../../utils/utils";

const User = ({ fetchAllMemberFromTicket }) => {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roles: [],
  });
  const [editMode, setEditMode] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({ id: null, email: "" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const fetchAllMember = useCallback(async () => {
    const response = await fetch(
      `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users?q=tenant:` + tenant(),
      {
        method: "GET",
        headers: oauthUserMgmt(),
      },
    );
  
    if (response.ok) {
      const result = await response.json();
      
      const filteredUsers = result.filter(user => !user.email.endsWith("@apicall.com"));

      const usersWithFullNameAndId = filteredUsers
        ? filteredUsers
            .filter((item) => item.firstName)
            .map((user) => ({
              id: user.id,
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              roles: [],
            }))
        : [];
  
      const usersWithRoles = await Promise.all(
        usersWithFullNameAndId.map(async (user) => {
          const rolesResponse = await fetch(
            `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${user.id}/role-mappings/realm`,
            {
              method: "GET",
              headers: oauthUserMgmt(),
            },
          );
  
          if (rolesResponse.ok) {
            const roles = await rolesResponse.json();
            return {
              ...user,
              roles: roles.map((role) => role.name),
            };
          }
          return user;
        }),
      );
  
      setUsers(usersWithRoles); // Ensure fresh data is set, instead of appending
    }
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/roles`,
        {
          method: "GET",
          headers: oauthUserMgmt(),
        },
      );
      if (response.ok) {
        const rolesData = await response.json();
        const filteredRoles = rolesData.filter(
          (role) => !role.name.includes("_") && !role.name.includes("-"),
        );
        const formattedRoles = filteredRoles.map((role) => ({
          id: role.id,
          value: role.name,
          label: role.name.charAt(0).toUpperCase() + role.name.slice(1),
        }));
        setRoles(formattedRoles);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    fetchAllMember();
    fetchRoles();
  }, [fetchAllMember]);

  const generateRandomPassword = (length = 12) => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    return Array.from(crypto.getRandomValues(new Uint8Array(length)))
      .map((x) => chars[x % chars.length])
      .join("");
  };

  const addUser = async () => {
    if (newUser.email && newUser.firstName && newUser.lastName) {

      const randomPassword = generateRandomPassword();
      console.log("log password", randomPassword)

      const response = await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users`,
        {
          method: "POST",
          headers: {
            ...oauthUserMgmt(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: newUser.firstName,
            lastName: newUser.lastName,
            email: newUser.email,
            username: newUser.email,
            enabled: true,
            emailVerified: true,
            attributes: { tenant: tenant() },
          }),
        }
      );


      if (response.ok) {
        fetchAllMemberFromTicket();
        const userIdResponse = await fetch(
          `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users?username=${newUser.email}`,
          {
            method: "GET",
            headers: {
              ...oauthUserMgmt(),
            },
          },
        );
        const createdUser = (await userIdResponse.json())[0];

        const userId = createdUser.id;

        await fetch(
          `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${userId}/reset-password`,
          {
            method: "PUT",
            headers: {
              ...oauthUserMgmt(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              type: "password",
              value: randomPassword,
              temporary: false, // Forces the user to change password
            }),
          },
        );

        const selectedRoles = roles.filter((role) =>
          Array.isArray(newUser.roles)
            ? newUser.roles.includes(role.value)
            : [newUser.roles].includes(role.value),
        );

        const rolePayload = selectedRoles.map((role) => ({
          id: role.id,
          name: role.value,
        }));

        const roleResponse = await fetch(
          `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${userId}/role-mappings/realm`,
          {
            method: "POST",
            headers: {
              ...oauthUserMgmt(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify(rolePayload),
          },
        );

        await fetch(
          `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${userId}`,
          {
            method: "PUT",
            headers: {
              ...oauthUserMgmt(),
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              requiredActions: ["UPDATE_PASSWORD"], // Enforce password update
            }),
          }
        );

        if (roleResponse.ok) {

          await sendMailNotification(newUser.email, newUser.firstName, randomPassword, "Register");

          setUsers([...users, { ...newUser, id: userId }]);
          setNewUser({ firstName: "", lastName: "", email: "", roles: [] });
          setIsAddingUser(false);
        }
      }
    }
  };

  const sendMailNotification = async (email, firstName, password, type) => {
    try {
      const templateName = type === "Register" ? "User-Registeration" : "Reset-Password";
      const subject = type === "Register" ? "Welcome" : "Reset";

      const response = await fetch(
        `${process.env.REACT_APP_NOTIFICATION}/notification/save?templatePath=/opt/eVyoog/systemconfig/mailTemplate/Ticket/${templateName}.html`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...headers(),
          },
          body: JSON.stringify({
            recipient: email,  // Make sure this is the correct recipient email
            message: "",
            type: "EMAIL",
            subject: `${subject}! Your Account Details`,
            status: "PENDING",
            placeholders: {
              user: firstName,
              email: email,
              password: password,
            },
          }),
        },
      );

      if (!response.ok) {
        console.error("Failed to send email notification.");
      } else {
        console.log("Email notification sent successfully.");
      }
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };



  const handleEditClick = (user) => {
    setEditMode(user.id);
    setEditUserData(user);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditUserData({ ...editUserData, [name]: value });
  };

  const handleSave = async (userId) => {
    try {
      await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${userId}`,
        {
          method: "PUT",
          headers: {
            ...oauthUserMgmt(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: editUserData.firstName,
            lastName: editUserData.lastName,
            email: editUserData.email,
            username: editUserData.email,
            attributes: { tenant: tenant() },
          }),
        },
      );

      const selectedRoles = roles.filter((role) =>
        editUserData.roles.includes(role.value),
      );

      const rolePayload = selectedRoles.map((role) => ({
        id: role.id,
        name: role.value,
      }));

      const assignRolesResponse = await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${userId}/role-mappings/realm`,
        {
          method: "POST",
          headers: {
            ...oauthUserMgmt(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(rolePayload),
        },
      );

      if (assignRolesResponse.ok) {
        console.log(`Roles updated for user ${userId} successfully.`);

        setUsers(
          users.map((user) =>
            user.id === userId
              ? {
                ...editUserData,
                roles: selectedRoles.map((role) => role.value),
              }
              : user,
          ),
        );
        setEditMode(null);
        setEditUserData({});
      }
    } catch (error) {
      console.error("Error while updating user or assigning roles:", error);
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${userId}`,
        {
          method: "DELETE",
          headers: oauthUserMgmt(),
        },
      );

      if (response.ok) {
        fetchAllMemberFromTicket();
        setUsers(users.filter((user) => user.id !== userId));
      }
    } catch (error) {
      console.error("An error occurred while deleting the user:", error);
    }
  };

  const handleOpenConfirmDialog = (userId, email) => {
    setSelectedUser({ id: userId, email });
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedUser({ id: null, email: "" });
  };

  const handleConfirmResetPassword = async () => {
    setConfirmDialogOpen(false);
    await handleResetPassword(selectedUser.id, selectedUser.email, selectedUser.firstName);
  };

  const handleResetPassword = async (userId, email, firstName) => {
    console.log("email", email);
    console.log("userId", userId);
    const randomPassword = generateRandomPassword(); // Generate a new random password

    console.log("Resetting password for:", email);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${userId}/reset-password`,
        {
          method: "PUT",
          headers: oauthHeaders(),
          body: JSON.stringify({
            type: "password",
            value: randomPassword,
          }),
        },
      );

      await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${userId}`,
        {
          method: "PUT",
          headers: {
            ...oauthUserMgmt(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            requiredActions: ["UPDATE_PASSWORD"], // Enforce password update
          }),
        }
      );

      if (response.ok) {
        console.log("Password reset successfully");
        await sendMailNotification(email, firstName, randomPassword, "Reset");
      } else {
        console.error("Failed to reset password");
      }
    } catch (error) {
      console.error("Network error:", error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = users.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <div>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  borderBottom: "2px solid #ddd",
                }}
              >
                First Name
              </TableCell>
              <TableCell
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  borderBottom: "2px solid #ddd",
                }}
              >
                Last Name
              </TableCell>
              <TableCell
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  borderBottom: "2px solid #ddd",
                }}
              >
                Email
              </TableCell>
              <TableCell
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  borderBottom: "2px solid #ddd",
                }}
              >
                Role
              </TableCell>
              <TableCell
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  borderBottom: "2px solid #ddd",
                }}
              >
                Reset
              </TableCell>
              <TableCell
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  borderBottom: "2px solid #ddd",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.map((user) => (
              <TableRow
                key={user.id}
                style={{ borderBottom: "1px solid #ddd" }}
              >
                <TableCell style={{ borderBottom: "none" }}>
                  {editMode === user.id ? (
                    <TextField
                      name="firstName"
                      variant="standard"
                      value={editUserData.firstName}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  ) : (
                    user.firstName
                  )}
                </TableCell>
                <TableCell style={{ borderBottom: "none" }}>
                  {editMode === user.id ? (
                    <TextField
                      name="lastName"
                      variant="standard"
                      value={editUserData.lastName}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  ) : (
                    user.lastName
                  )}
                </TableCell>
                <TableCell style={{ borderBottom: "none" }}>
                  {user.email}
                </TableCell>
                <TableCell style={{ borderBottom: "none" }}>
                  {editMode === user.id ? (
                    <TextField
                      name="roles"
                      variant="standard"
                      select
                      value={editUserData.roles}
                      onChange={handleInputChange}
                      fullWidth
                      SelectProps={{
                        multiple: true,
                      }}
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.value} value={role.value}>
                          {role.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    user.roles
                      .filter((role) => role.indexOf("default-role") === -1)
                      .join(", ")
                  )}
                </TableCell>
                <TableCell
                  style={{ textAlign: "center", borderBottom: "none" }}
                >
                  {editMode !== user.id && (
                    <Tooltip title="Reset Password">
                      <IconButton
                        onClick={() =>
                          handleOpenConfirmDialog(user.id, user.email)
                        }
                        color="default"
                      >
                        <VpnKeyIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Dialog
                    open={confirmDialogOpen}
                    onClose={handleCloseConfirmDialog}
                  >
                    <DialogTitle>Confirm Reset Password</DialogTitle>
                    <DialogContent>
                      Are you sure you want to reset the password for{" "}
                      {selectedUser.email}?
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={handleCloseConfirmDialog}
                        color="secondary"
                      >
                        No
                      </Button>
                      <Button
                        onClick={handleConfirmResetPassword}
                        color="primary"
                      >
                        Yes
                      </Button>
                    </DialogActions>
                  </Dialog>
                </TableCell>
                <TableCell
                  style={{ borderBottom: "none", whitespace: "nowrap" }}
                >
                  {editMode === user.id ? (
                    <IconButton
                      onClick={() => handleSave(user.id)}
                      color="primary"
                    >
                      <SaveIcon />
                    </IconButton>
                  ) : (
                    <>
                      <IconButton
                        onClick={() => handleEditClick(user)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => deleteUser(user.id)}
                        color="secondary"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {isAddingUser && (
              <TableRow style={{ borderBottom: "none" }}>
                <TableCell style={{ borderBottom: "none" }}>
                  <TextField
                    label="First Name"
                    variant="standard"
                    fullWidth
                    value={newUser.firstName}
                    onChange={(e) =>
                      setNewUser({ ...newUser, firstName: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell style={{ borderBottom: "none" }}>
                  <TextField
                    label="Last Name"
                    variant="standard"
                    fullWidth
                    value={newUser.lastName}
                    onChange={(e) =>
                      setNewUser({ ...newUser, lastName: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell style={{ borderBottom: "none" }}>
                  <TextField
                    label="Email"
                    variant="standard"
                    fullWidth
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                  />
                </TableCell>

                <TableCell style={{ borderBottom: "none" }}>
                  <TextField
                    label="Role"
                    variant="standard"
                    fullWidth
                    select
                    value={newUser.roles}
                    onChange={(e) =>
                      setNewUser({ ...newUser, roles: e.target.value })
                    }
                    SelectProps={{
                      multiple: true,
                    }}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell style={{ borderBottom: "none" }}>
                  <IconButton color="primary" onClick={addUser}>
                    <SaveIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!isAddingUser && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setIsAddingUser(true)}
          style={{ marginTop: "10px" }}
        >
          Add User
        </Button>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default User;
